import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { BookOpen, Upload, History, Loader2, Sparkles, Pencil, X, Eye, EyeOff, RefreshCw } from "lucide-react";

const UPLOAD_ROLES = ["Super Admin", "Admin", "Pollen Dots User"];
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

function DocCard({ doc, canUpload, onChanged }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);

  const token = localStorage.getItem("session_token");
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/playbook/docs/${doc.slug}/file?auth=${token}&v=${doc.version}`;

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) return toast.error("Only PDF files are accepted");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/playbook/docs/${doc.slug}/upload`, fd);
      toast.success(`${doc.title} replaced — now v${doc.version + 1}. AI is reading it…`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const saveEdits = async () => {
    try {
      await api.patch(`/admin/playbook/docs/${doc.slug}`, form);
      toast.success("Saved");
      setEditing(false);
      onChanged();
    } catch { toast.error("Save failed"); }
  };

  const refreshSummary = async () => {
    try {
      await api.post(`/admin/playbook/docs/${doc.slug}/refresh-summary`);
      toast.success("AI is re-reading the document…");
      onChanged();
    } catch { toast.error("Refresh failed"); }
  };

  const ai = doc.ai_summary;

  return (
    <div data-testid={`playbook-card-${doc.slug}`} className="rounded-xl border border-forest/10 bg-beige overflow-hidden">
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-full bg-forest text-cream flex items-center justify-center font-serif text-lg">{doc.read_order}</div>
            <div className="min-w-0">
              <div className="font-serif text-2xl text-ink">{doc.title}</div>
              <div className="text-xs text-inkmute mt-0.5">
                {doc.version > 0 ? (
                  <>v{doc.version} · {doc.filename} ({fmtSize(doc.file_size)}) · uploaded {new Date(doc.uploaded_at).toLocaleDateString()} by {doc.uploaded_by}</>
                ) : (
                  <span className="text-red-600">No file uploaded yet</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {doc.version > 0 && (
              <button data-testid={`playbook-view-${doc.slug}`} onClick={() => setViewing(!viewing)}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-forest/25 text-forest hover:bg-forest hover:text-cream transition-colors">
                {viewing ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {viewing ? "Close" : "Read"}
              </button>
            )}
            {canUpload && (
              <>
                <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={onFile} data-testid={`playbook-file-${doc.slug}`} />
                <button data-testid={`playbook-upload-${doc.slug}`} onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-forest text-cream hover:bg-forestDeep disabled:opacity-50">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {uploading ? "Uploading…" : doc.version > 0 ? "Replace" : "Upload"}
                </button>
                <button onClick={() => { setEditing(!editing); setForm({ purpose: doc.purpose, when_to_use: doc.when_to_use, who_uses: doc.who_uses }); }}
                  className="text-inkmute hover:text-forest" title="Edit descriptions"><Pencil className="w-4 h-4" /></button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="mt-4 space-y-3">
            {[["purpose", "What it is"], ["when_to_use", "When to use it"], ["who_uses", "Who uses it"]].map(([k, label]) => (
              <label key={k} className="block">
                <div className="text-[10px] uppercase tracking-widest text-clay mb-1">{label}</div>
                <textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={2}
                  className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm text-ink" />
              </label>
            ))}
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-widest px-3 py-1.5 border border-forest/20 rounded-full">Cancel</button>
              <button onClick={saveEdits} className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-forest text-cream">Save</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[["What it is", doc.purpose], ["When to use it", doc.when_to_use], ["Who uses it", doc.who_uses]].map(([label, text]) => (
              <div key={label} className="rounded-lg bg-cream border border-forest/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-clay mb-1">{label}</div>
                <div className="text-sm text-ink leading-relaxed">{text}</div>
              </div>
            ))}
          </div>
        )}

        {doc.ai_status === "pending" && (
          <div className="mt-3 flex items-center gap-2 text-xs text-inkmute"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI is reading the new version…</div>
        )}
        {doc.ai_status === "failed" && canUpload && (
          <button onClick={refreshSummary} className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-600 link-underline"><RefreshCw className="w-3.5 h-3.5" /> AI summary failed — retry</button>
        )}
        {ai && doc.ai_status === "done" && (
          <div data-testid={`playbook-ai-${doc.slug}`} className="mt-4 rounded-lg border border-pollen/60 bg-cream p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-clay flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-forest" /> What's inside · v{doc.version}</div>
              {canUpload && <button onClick={refreshSummary} className="text-inkmute hover:text-forest" title="Regenerate summary"><RefreshCw className="w-3.5 h-3.5" /></button>}
            </div>
            <p className="text-sm text-ink leading-relaxed">{ai.overview}</p>
            {(ai.key_sections || []).length > 0 && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ai.key_sections.map((s, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-medium text-ink">{s.title}</span>
                    {s.note && <span className="text-inkmute"> — {s.note}</span>}
                  </div>
                ))}
              </div>
            )}
            {ai.new_joiner_tip && (
              <div className="mt-3 text-xs text-forest bg-beige rounded-lg px-3 py-2 border border-forest/10">
                <span className="font-semibold uppercase tracking-widest text-[9px] mr-1.5">New here?</span>{ai.new_joiner_tip}
              </div>
            )}
          </div>
        )}

        {(doc.history || []).length > 1 && (
          <button onClick={() => setShowHistory(!showHistory)} className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-forest link-underline">
            <History className="w-3.5 h-3.5" /> {showHistory ? "Hide versions" : `Version history (${doc.history.length})`}
          </button>
        )}
        {showHistory && (
          <div className="mt-2 rounded-lg bg-cream border border-forest/10 divide-y divide-forest/10">
            {[...doc.history].reverse().map((h) => (
              <div key={h.version} className="px-3 py-2 text-xs text-inkmute flex flex-wrap gap-2 justify-between">
                <span className="text-ink font-medium">v{h.version} · {h.filename}</span>
                <span>{fmtSize(h.file_size)} · {new Date(h.uploaded_at).toLocaleString()} · {h.uploaded_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewing && (
        <div className="border-t border-forest/10 bg-cream p-2">
          <iframe title={doc.title} src={fileUrl} className="w-full h-[75vh] rounded-lg border border-forest/10 bg-white" />
        </div>
      )}
    </div>
  );
}

export default function PlaybookTab() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const canUpload = UPLOAD_ROLES.includes(user?.role);

  const load = () => api.get("/admin/playbook/docs").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!docs.some((d) => d.ai_status === "pending")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [docs]);

  return (
    <div data-testid="playbook-tab">
      <div className="mb-6 rounded-xl border border-forest/15 bg-forest text-cream p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] opacity-80"><BookOpen className="w-4 h-4" /> Content Playbook · Start here</div>
        <p className="text-sm mt-2 max-w-3xl leading-relaxed opacity-95">
          These five documents are the entire content programme, in reading order. New to the team? Read them top to bottom —
          each card tells you what the document is, when to reach for it, and what's inside the current version.
          When a new master file or register is published, upload it here: the old version is replaced and the whole system updates itself.
        </p>
      </div>
      <div className="space-y-4">
        {docs.length === 0 && <div className="text-inkmute text-sm">Loading playbook…</div>}
        {docs.map((d) => <DocCard key={d.slug} doc={d} canUpload={canUpload} onChanged={load} />)}
      </div>
    </div>
  );
}
